import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { getTokens, INK } from "../../styles/tokens";

/* Blocos do "modal padrão" e da "página padrão" do painel usados pela frente
 * Plataforma/Integrações/Acesso. Moram aqui porque a frente não pode criar
 * pasta compartilhada nova; o lugar natural é components/ui. */

export const usePanelStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const dark = t.mode === "dark";
  const strong = dark ? t.text : INK[800];
  return {
    // ----- título de modal -----
    modalTitle: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
    },
    modalTitleText: { minWidth: 0 },
    modalHeading: {
      margin: 0,
      fontFamily: t.font,
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: t.text,
    },
    modalSubtitle: {
      margin: "3px 0 0",
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.45,
      letterSpacing: 0,
      color: t.textSecondary,
    },
    modalClose: { flex: "none", margin: "-4px -8px -4px 0", padding: 7 },

    // ----- campo com rótulo acima -----
    field: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      minWidth: 0,
      // o rótulo já dá o respiro: sem a margem do margin="dense"
      "& > .MuiFormControl-root, & > .MuiTextField-root": { margin: 0 },
    },
    label: {
      fontFamily: t.font,
      fontSize: 12.5,
      fontWeight: 600,
      lineHeight: 1.35,
      color: dark ? t.label : INK[700],
    },
    required: { marginLeft: 3, color: t.semantic.error },
    optional: { marginLeft: 6, fontWeight: 500, color: t.textMuted },
    help: {
      fontFamily: t.font,
      fontSize: 12,
      lineHeight: 1.45,
      color: t.textSecondary,
    },
    fieldError: {
      fontFamily: t.font,
      fontSize: 12,
      lineHeight: 1.45,
      color: dark ? t.status.down.fg : t.semantic.error,
    },

    // ----- grade e seções de formulário -----
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: 16,
      rowGap: 14,
      alignItems: "start",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    grid3: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    grid1: { gridTemplateColumns: "minmax(0, 1fr)" },
    full: { gridColumn: "1 / -1" },
    stack: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
    section: {
      minWidth: 0,
      "& + &": {
        marginTop: 20,
        paddingTop: 18,
        borderTop: `1px solid ${t.line}`,
      },
    },
    sectionHead: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8,
      marginBottom: 12,
    },
    sectionTitle: {
      margin: 0,
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: strong,
    },
    sectionDesc: {
      margin: "2px 0 0",
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.45,
      color: t.textSecondary,
    },

    // ----- linha de chave (rótulo + descrição à esquerda, controle à direita) -----
    switchRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      minWidth: 0,
      padding: "8px 12px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.paper,
    },
    switchText: { minWidth: 0 },
    switchLabel: {
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 500,
      lineHeight: 1.4,
      color: strong,
    },

    // ----- aviso em caixa -----
    callout: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: t.textSecondary,
      "& .MuiSvgIcon-root": { fontSize: 18, flex: "none", marginTop: 1 },
    },
    calloutWarn: {
      borderColor: "transparent",
      backgroundColor: t.status.warn.bg,
      color: t.status.warn.fg,
    },
    calloutDown: {
      borderColor: "transparent",
      backgroundColor: t.status.down.bg,
      color: t.status.down.fg,
    },
    calloutOk: {
      borderColor: "transparent",
      backgroundColor: t.status.ok.bg,
      color: t.status.ok.fg,
    },

    // <form> entre o Dialog e o conteúdo: sem isto o corpo não rola sozinho e o rodapé some da vista
    form: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "hidden",
    },

    // ----- rodapé de modal -----
    actionsLeft: { marginRight: "auto", display: "flex", alignItems: "center", gap: 8 },
    // botão com progresso por cima (o rótulo fica, apagado pelo disabled)
    busyWrap: { position: "relative" },
    busy: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
      color: t.brand,
    },
    danger: {
      color: dark ? t.status.down.fg : t.semantic.error,
      "&:hover": { backgroundColor: t.status.down.bg },
    },
    dangerContained: {
      background: t.semantic.error,
      backgroundColor: t.semantic.error,
      color: "#ffffff",
      boxShadow: "none",
      "&:hover": { background: "#b91c1c", backgroundColor: "#b91c1c", boxShadow: "none" },
    },

    // ----- página: barra de filtros, cartão de tabela, ações por linha -----
    toolbar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 14,
      "& > .MuiTextField-root, & > .MuiFormControl-root": { margin: 0 },
    },
    toolbarGrow: { flex: "1 1 220px", minWidth: 0, maxWidth: 360 },
    toolbarSpacer: { flex: "1 1 auto" },
    tableCard: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: "auto",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      ...theme.scrollbarStyles,
      "& .MuiTableCell-head": { whiteSpace: "nowrap" },
      "& .MuiTableRow-root:last-child > .MuiTableCell-body": { borderBottom: 0 },
    },
    // cartão de tabela que não estica (página com várias seções)
    tableCardAuto: { flex: "none" },
    rowActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 2,
      whiteSpace: "nowrap",
      "& .MuiIconButton-root": { padding: 7, color: t.textSecondary },
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    cellStrong: { fontWeight: 600, color: strong },
    cellMuted: { color: t.textSecondary, fontSize: 13 },
    mono: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 12.5,
    },
    empty: {
      padding: "40px 20px",
      textAlign: "center",
      fontFamily: t.font,
      fontSize: 13.5,
      color: t.textSecondary,
    },
  };
});

const cx = (...parts) => parts.filter(Boolean).join(" ");

/** Título de modal do painel: frase curta, subtítulo opcional e "fechar" opcional. */
export const ModalTitle = ({ title, subtitle, onClose, closeLabel = "Fechar", id, children }) => {
  const classes = usePanelStyles();
  return (
    <DialogTitle disableTypography id={id}>
      <div className={classes.modalTitle}>
        <div className={classes.modalTitleText}>
          <h2 className={classes.modalHeading}>{title}</h2>
          {subtitle && <p className={classes.modalSubtitle}>{subtitle}</p>}
          {children}
        </div>
        {onClose && (
          <IconButton className={classes.modalClose} onClick={onClose} aria-label={closeLabel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    </DialogTitle>
  );
};

/**
 * Campo com rótulo acima. O controle (TextField/Select sem `label`) vai em `children`.
 * `help` = ajuda curta; `error` = mensagem de erro (substitui a ajuda); `full` ocupa a linha da grade.
 */
export const FormField = ({ label, help, error, required, optional, htmlFor, full, className, children }) => {
  const classes = usePanelStyles();
  return (
    <div className={cx(classes.field, full && classes.full, className)}>
      {label && (
        <label className={classes.label} htmlFor={htmlFor}>
          {label}
          {required && (
            <span className={classes.required} aria-hidden="true">
              *
            </span>
          )}
          {optional && <span className={classes.optional}>{optional}</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className={classes.fieldError} role="alert">
          {error}
        </span>
      ) : (
        help && <span className={classes.help}>{help}</span>
      )}
    </div>
  );
};

/** Grade de campos: 2 colunas (1 no celular). `columns` = 1 | 2 | 3. */
export const FormGrid = ({ columns = 2, className, children, ...rest }) => {
  const classes = usePanelStyles();
  return (
    <div
      className={cx(
        classes.grid,
        columns === 3 && classes.grid3,
        columns === 1 && classes.grid1,
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

/** Seção de formulário: título pequeno, descrição e ação opcionais; seções vizinhas ganham filete. */
export const FormSection = ({ title, description, action, className, children }) => {
  const classes = usePanelStyles();
  return (
    <section className={cx(classes.section, className)}>
      {(title || action) && (
        <div className={classes.sectionHead}>
          <div style={{ minWidth: 0 }}>
            {title && <h3 className={classes.sectionTitle}>{title}</h3>}
            {description && <p className={classes.sectionDesc}>{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
};

/** Chave com rótulo e descrição à esquerda; o controle (Switch/Checkbox) vai em `children`. */
export const SwitchRow = ({ label, help, full, className, children }) => {
  const classes = usePanelStyles();
  return (
    <div className={cx(classes.switchRow, full && classes.full, className)}>
      <div className={classes.switchText}>
        <div className={classes.switchLabel}>{label}</div>
        {help && <div className={classes.help}>{help}</div>}
      </div>
      {children}
    </div>
  );
};

/** Aviso em caixa. `tone`: neutral (padrão) | warn | down | ok. */
export const Callout = ({ tone = "neutral", icon, full, className, children }) => {
  const classes = usePanelStyles();
  const toneClass =
    tone === "warn"
      ? classes.calloutWarn
      : tone === "down"
      ? classes.calloutDown
      : tone === "ok"
      ? classes.calloutOk
      : null;
  return (
    <div className={cx(classes.callout, toneClass, full && classes.full, className)}>
      {icon}
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
};
